import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useReadyState } from './ReadyStateContext';
import {
  useAdminInitialData,
  getTagEnglishName,
  type Language,
  type Locale,
  type SkillLevel,
  type SkillLevelLocalization,
  type Tag,
  type LearningGoal,
  type LearningGoalLocalization,
  type EpisodeDuration,
  type EpisodeDurationLocalization,
} from '../services/initialDataApi';

// Re-export types for convenience
export type {
  Language,
  Locale,
  SkillLevel,
  SkillLevelLocalization,
  Tag,
  LearningGoal,
  LearningGoalLocalization,
  EpisodeDuration,
  EpisodeDurationLocalization,
};

/**
 * Simplified tag shape derived from initial data, used by episode views.
 * Provides a flat `name` (English) and camelCase `learningGoalIds` for convenience.
 */
export interface SimpleTag {
  id: number;
  name: string;
  learningGoalIds: number[];
}

export interface VoiceTestTemplate {
  label: string;
  text: string;
}

export interface InitialData {
  appVersion: string;
  environment: string;
  languages: Language[];
  locales: Locale[];
  skillLevels: SkillLevel[];
  tags: Tag[];
  learningGoals: LearningGoal[];
  episodeDurations: EpisodeDuration[]; // Story 15.11
  voiceTestTemplates: Record<string, VoiceTestTemplate[]>;
}

export interface InitialDataState {
  data: InitialData | null;
  isLoading: boolean;
  error: string | null;
}

export interface InitialDataContextType {
  state: InitialDataState;
  refetch: () => void;
}

const InitialDataContext = createContext<InitialDataContextType | undefined>(undefined);

interface InitialDataProviderProps {
  children: ReactNode;
}

export function InitialDataProvider({ children }: InitialDataProviderProps) {
  const { markAppReady } = useReadyState();
  const { data: apiData, isLoading, error, refetch } = useAdminInitialData();

  // Mark app as ready when initial data is loaded (success or error)
  useEffect(() => {
    if (!isLoading) {
      markAppReady();
    }
  }, [isLoading, markAppReady]);

  // Merge local app configuration with API response
  const mergedData: InitialData | null = apiData
    ? {
        // Local configuration from environment variables (app-specific)
        appVersion: import.meta.env.VITE_APP_VERSION || '1.0.0',
        environment: import.meta.env.VITE_ENVIRONMENT || 'development',
        // Backend data (languages, locales, skill levels, tags, learning goals, episode durations)
        languages: apiData.languages,
        locales: apiData.locales,
        skillLevels: apiData.skill_levels,
        tags: apiData.tags,
        learningGoals: apiData.learning_goals,
        episodeDurations: apiData.episode_durations, // Story 15.11
        // Voice test templates (hardcoded per language for now)
        voiceTestTemplates: {
          en: [
            {
              label: 'Default',
              text: 'Hello! This is a test of the text-to-speech system. The quick brown fox jumps over the lazy dog.',
            },
            {
              label: 'Greeting',
              text: 'Hello! Welcome to our application. How can I help you today?',
            },
            { label: 'Question', text: 'What would you like to know more about?' },
            {
              label: 'Instruction',
              text: 'Please follow these steps to complete the setup process.',
            },
            {
              label: 'Confirmation',
              text: 'Your request has been successfully processed. Thank you!',
            },
            { label: 'Error', text: 'Sorry, something went wrong. Please try again later.' },
          ],
          es: [
            {
              label: 'Predeterminado',
              text: 'Hola! Esta es una prueba del sistema de texto a voz. El rápido zorro marrón salta sobre el perro perezoso.',
            },
            {
              label: 'Saludo',
              text: '¡Hola! Bienvenido a nuestra aplicación. ¿Cómo puedo ayudarte hoy?',
            },
            { label: 'Pregunta', text: '¿Sobre qué te gustaría saber más?' },
            {
              label: 'Instrucción',
              text: 'Por favor, sigue estos pasos para completar el proceso de configuración.',
            },
            {
              label: 'Confirmación',
              text: '¡Tu solicitud ha sido procesada exitosamente. Gracias!',
            },
            {
              label: 'Error',
              text: 'Lo siento, algo salió mal. Por favor, inténtalo de nuevo más tarde.',
            },
          ],
          fr: [
            {
              label: 'Par défaut',
              text: 'Bonjour! Ceci est un test du système de synthèse vocale. Le rapide renard brun saute par-dessus le chien paresseux.',
            },
            {
              label: 'Salutation',
              text: "Bonjour! Bienvenue dans notre application. Comment puis-je vous aider aujourd'hui?",
            },
            { label: 'Question', text: 'Sur quoi aimeriez-vous en savoir plus?' },
            {
              label: 'Instruction',
              text: 'Veuillez suivre ces étapes pour terminer le processus de configuration.',
            },
            {
              label: 'Confirmation',
              text: 'Votre demande a été traitée avec succès. Merci!',
            },
            {
              label: 'Erreur',
              text: "Désolé, quelque chose s'est mal passé. Veuillez réessayer plus tard.",
            },
          ],
          de: [
            {
              label: 'Standard',
              text: 'Hallo! Dies ist ein Test des Text-zu-Sprache-Systems. Der schnelle braune Fuchs springt über den faulen Hund.',
            },
            {
              label: 'Begrüßung',
              text: 'Hallo! Willkommen in unserer Anwendung. Wie kann ich Ihnen heute helfen?',
            },
            { label: 'Frage', text: 'Worüber möchten Sie mehr erfahren?' },
            {
              label: 'Anweisung',
              text: 'Bitte befolgen Sie diese Schritte, um den Einrichtungsprozess abzuschließen.',
            },
            {
              label: 'Bestätigung',
              text: 'Ihre Anfrage wurde erfolgreich bearbeitet. Vielen Dank!',
            },
            {
              label: 'Fehler',
              text: 'Entschuldigung, etwas ist schief gelaufen. Bitte versuchen Sie es später erneut.',
            },
          ],
        },
      }
    : null;

  const state: InitialDataState = {
    data: mergedData,
    isLoading,
    error: error ? error.message : null,
  };

  const value: InitialDataContextType = {
    state,
    refetch,
  };

  return <InitialDataContext.Provider value={value}>{children}</InitialDataContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useInitialData(): InitialDataContextType {
  const context = useContext(InitialDataContext);
  if (context === undefined) {
    throw new Error('useInitialData must be used within an InitialDataProvider');
  }
  return context;
}

/**
 * Convenience hook that returns all tags from initial data as SimpleTag[].
 * Replaces useTags() from episodesApi which was limited to 100 tags.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useTagsFromInitialData(): SimpleTag[] {
  const { state } = useInitialData();
  return useMemo(() => {
    const tags = state.data?.tags ?? [];
    return tags.map((tag) => ({
      id: tag.id,
      name: getTagEnglishName(tag),
      learningGoalIds: tag.learning_goal_ids,
    }));
  }, [state.data?.tags]);
}
